
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useSite } from "./siteContext";
import { useAuth } from "./authContext";
import { getUserCart, updateUserCart } from "../services/cartService";

export const CartContext = createContext();

// Helper function to get product ID, handling both 'ProductID' and 'product_id'
const getNormalizedProductId = (item) => item?.ProductID || item?.product_id;

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [userId, setUserId] = useState(null);
  const { selectedSite } = useSite();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const debounceTimer = useRef(null);

  const cartCount = useMemo(() => {
    if (!cartItems || cartItems.length === 0) {
      return 0;
    }
    const filteredItems = cartItems.filter(
      (item) => item.Site === selectedSite
    );
    return filteredItems.reduce(
      (total, item) => total + (parseInt(item.Quantity) || 0),
      0
    );
  }, [cartItems, selectedSite]);

  const fetchCart = async (userId) => {
    if (!userId) return;
    setIsLoading(true);
    try {
      const response = await getUserCart(userId);
      const serverCart = Array.isArray(response?.data) ? response.data : [];
      setCartItems(serverCart);
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  };

  useEffect(() => {
    if (user) {
      setUserId(user.user_id);
    }
  }, [user]);

  useEffect(() => {
    if (userId) {
      fetchCart(userId);
    }
  }, [userId]);

  const refreshCart = useCallback(() => {
    if (userId) {
      fetchCart(userId);
    }
  }, [userId]);

  const updateCart = async (newCartItems) => {
    setCartItems(newCartItems); // Optimistic UI update

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(async () => {
      if (userId) {
        try {
          await updateUserCart(userId, newCartItems);
        } catch (error) {
          console.error("Error updating cart on the server:", error);
          // Optional: Revert to the server state if the update fails
          fetchCart(userId);
        }
      }
    }, 500); // 500ms debounce delay
  };

  const addToCart = async (product) => {
    const productToAdd = {
      ...product,
      Quantity: parseInt(product.Quantity) || 1,
    };

    const existingProductIndex = cartItems.findIndex(
      (item) =>
        getNormalizedProductId(item) === getNormalizedProductId(productToAdd) &&
        item.Site === selectedSite
    );

    let newCartItems;
    if (existingProductIndex > -1) {
      newCartItems = cartItems.map((item, index) =>
        index === existingProductIndex
          ? {
              ...item,
              Quantity:
                parseInt(item.Quantity) + parseInt(productToAdd.Quantity),
            }
          : item
      );
    } else {
      newCartItems = [...cartItems, productToAdd];
    }
    await updateCart(newCartItems);
  };

  const addMultipleToCart = async (products) => {
    let newCartItems = [...cartItems];
    products.forEach((product) => {
      const productQuantity = product.Quantity ?? 1;
      const existingProductIndex = newCartItems.findIndex(
        (item) =>
          getNormalizedProductId(item) === getNormalizedProductId(product) && item.Site === product.Site
      );

      if (existingProductIndex > -1) {
        newCartItems[existingProductIndex].Quantity += productQuantity;
      } else {
        newCartItems.push({ ...product, Quantity: productQuantity });
      }
    });
    await updateCart(newCartItems);
  };

  const removeFromCart = async (productId) => {
    const newCartItems = cartItems.filter(
      (item) =>
        !(getNormalizedProductId(item) === productId && item.Site === selectedSite)
    );
    await updateCart(newCartItems);
  };

  const clearCart = async () => {
    const newCartItems = cartItems.filter(
      (item) => item.Site !== selectedSite
    );
    await updateCart(newCartItems);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        cartCount,
        addToCart,
        removeFromCart,
        clearCart,
        updateCart,
        addMultipleToCart,
        isLoading,
        isInitialized,
        refreshCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
